import React from "react";
import { Link, Spacer } from "src/components";
import { useLocation } from "react-router-dom";
import { CaretDownOutlined } from "@ant-design/icons";
import { Dropdown, Menu } from "antd";
import intl from "react-intl-universal";
import { MARKETING_PAGES, MARKETING_PAGE_ROUTES } from "src/utils";
import { LinkText } from "./DesktopMenu";
import styled from "styled-components";

export interface Route {
    pathname: string;
    search: string;
}

export type MarketKind = "Sale" | "Lease";

const LISTING_MAP_PATHS = new Set([
    MARKETING_PAGE_ROUTES[MARKETING_PAGES.RESIDENTIAL],
    MARKETING_PAGE_ROUTES[MARKETING_PAGES.CONDO],
]);

export const isListingMapPath = (pathname: string) => LISTING_MAP_PATHS.has(pathname);

export const buildMarketNavHref = (
    pathname: string,
    search: string,
    market: MarketKind,
): string => {
    const onListing = isListingMapPath(pathname);
    const destPath = onListing
        ? pathname
        : MARKETING_PAGE_ROUTES[MARKETING_PAGES.RESIDENTIAL];
    const params = new URLSearchParams(onListing ? search : "");
    params.set("leaseOrSale", market);
    params.delete("isExclusive");
    params.delete("isLease");
    return `${destPath}?${params.toString()}`;
};

export const isMarketNavSelected = (
    pathname: string,
    search: string,
    market: MarketKind,
): boolean => {
    if (!isListingMapPath(pathname)) return false;
    const current = new URLSearchParams(search).get("leaseOrSale") || "Sale";
    return current === market;
};

interface NavbarItemProps {
    route?: Route | Route[];
    market?: MarketKind;
    text: string | string[];
    scrolly: number;
    icon: any;
}

export function isRoute(route: Route | Route[] | undefined): route is Route {
    return !!route && !Array.isArray(route) && "pathname" in route;
}

const NavbarItem: React.FC<NavbarItemProps> = ({ route, market, text, scrolly }) => {
    const { pathname, search } = useLocation();

    const isSameLocation = (route: Route) => {
        if (route.pathname !== pathname) {
            return false;
        }

        const routeSearch = new URLSearchParams(route.search);
        const curSearch = new URLSearchParams(search);

        let isSame = true;
        routeSearch.forEach((value, key) => {
            if (curSearch.get(key) !== value) {
                isSame = false;
            }
        });
        return isSame;
    };

    const isSameLocationGroup = (route: Route[]) => {
        let isSame = false;
        route.forEach((r) => {
            isSame = isSame || isSameLocation(r);
        });
        return isSame;
    };

    if (market && typeof text === "string") {
        return (
            <React.Fragment>
                <Link href={buildMarketNavHref(pathname, search, market)}>
                    <LinkText
                        $isRouteSelected={isMarketNavSelected(pathname, search, market)}
                        scrolly={scrolly}
                    >
                        {intl.get(text)}
                    </LinkText>
                </Link>
                <Spacer width={23} />
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            {isRoute(route) && typeof text === "string" && (
                <Link href={`${route.pathname}?${route.search}`}>
                    <LinkText $isRouteSelected={isSameLocation(route)} scrolly={scrolly}>
                        {intl.get(text)}
                    </LinkText>
                </Link>
            )}
            {!!route && !isRoute(route) && (
                <Dropdown
                    overlay={
                        <StyledMenu>
                            {route.map((r, i) => {
                                const useBaseLink =
                                    r.pathname.split("?")[0] === window.location.pathname;
                                return (
                                    <StyledMenuItem key={i}>
                                        <Link
                                            href={`${r.pathname}?${r.search}`}
                                            useBaseLink={useBaseLink}
                                        >
                                            {intl.get(text[i + 1])}
                                        </Link>
                                    </StyledMenuItem>
                                );
                            })}
                        </StyledMenu>
                    }
                    trigger={["hover"]}
                    getPopupContainer={(trigger) =>
                        trigger.parentElement ? trigger.parentElement : trigger
                    }
                >
                    <LinkText
                        $isRouteSelected={isSameLocationGroup(route)}
                        scrolly={scrolly}
                        style={{ cursor: "pointer" }}
                    >
                        {intl.get(text[0])}
                        <CaretDownOutlined
                            style={{
                                fontSize: "15px",
                                marginLeft: "7px",
                            }}
                        />
                    </LinkText>
                </Dropdown>
            )}
            <Spacer width={23} />
        </React.Fragment>
    );
};

export default NavbarItem;

const StyledMenu = styled(Menu)`
    border-radius: 8px;
`;

const StyledMenuItem = styled(Menu.Item)`
    padding: 8px 16px;
`;
